import { HoroscopeIconType } from '../Horoscopes/HoroscopesIcon/HoroscopesIcon-Constants';
import localstorage from '@/pages/Root/services/storage/localstorage';

const CLICK_HISTORY_STORAGE_KEY = 'horoscopes-click-history';

/**
 * Default date ranges for each zodiac sign (in English)
 */
const DEFAULT_DATE_RANGES: Record<HoroscopeIconType, string> = {
  [HoroscopeIconType.aries]: 'Mar 21st - Apr 19th',
  [HoroscopeIconType.taurus]: 'Apr 20th - May 20th',
  [HoroscopeIconType.gemini]: 'May 21st - Jun 20th',
  [HoroscopeIconType.cancer]: 'Jun 21st - Jul 22nd',
  [HoroscopeIconType.leo]: 'Jul 23rd - Aug 22nd',
  [HoroscopeIconType.virgo]: 'Aug 23rd - Sep 22nd',
  [HoroscopeIconType.libra]: 'Sep 23rd - Oct 22nd',
  [HoroscopeIconType.scorpio]: 'Oct 23rd - Nov 21st',
  [HoroscopeIconType.sagittarius]: 'Nov 22nd - Dec 21st',
  [HoroscopeIconType.capricorn]: 'Dec 22nd - Jan 19th',
  [HoroscopeIconType.aquarius]: 'Jan 20th - Feb 18th',
  [HoroscopeIconType.pisces]: 'Feb 19th - Mar 20th',
};

/**
 * Translation keys for date ranges (for i18n)
 */
const DATE_RANGE_TRANSLATION_KEYS: Record<HoroscopeIconType, string> = {
  [HoroscopeIconType.aries]: 'horoscope.ariesDateRange',
  [HoroscopeIconType.taurus]: 'horoscope.taurusDateRange',
  [HoroscopeIconType.gemini]: 'horoscope.geminiDateRange',
  [HoroscopeIconType.cancer]: 'horoscope.cancerDateRange',
  [HoroscopeIconType.leo]: 'horoscope.leoDateRange',
  [HoroscopeIconType.virgo]: 'horoscope.virgoDateRange',
  [HoroscopeIconType.libra]: 'horoscope.libraDateRange',
  [HoroscopeIconType.scorpio]: 'horoscope.scorpioDateRange',
  [HoroscopeIconType.sagittarius]: 'horoscope.sagittariusDateRange',
  [HoroscopeIconType.capricorn]: 'horoscope.capricornDateRange',
  [HoroscopeIconType.aquarius]: 'horoscope.aquariusDateRange',
  [HoroscopeIconType.pisces]: 'horoscope.piscesDateRange',
};

/**
 * Get the translated date range for a zodiac sign
 * @param sign - The zodiac sign
 * @param t - Translation function from react-i18next
 * @returns The translated date range string
 */
export const getTranslatedDateRange = (
  sign: HoroscopeIconType,
  t: any // Using any to handle react-i18next's complex TFunction type
): string => {
  const translationKey = DATE_RANGE_TRANSLATION_KEYS[sign];
  const defaultValue = DEFAULT_DATE_RANGES[sign];
  return t(translationKey, defaultValue);
};

/**
 * Get the click history from localStorage
 * @returns Array of signs in order of clicks (most recent first)
 */
export const getClickHistory = (): HoroscopeIconType[] => {
  const history = localstorage?.get(CLICK_HISTORY_STORAGE_KEY);
  if (Array.isArray(history)) {
    return history.filter((sign) => {
      return Object.values(HoroscopeIconType).includes(sign);
    });
  }
  return [];
};

/**
 * Record a sign click and update localStorage
 * Moves the clicked sign to the front of the history (most recent first)
 * @param sign - The sign that was clicked
 */
export const recordSignClick = (sign: HoroscopeIconType): void => {
  const history = getClickHistory();
  // Remove the sign if it already exists in history
  const filteredHistory = history.filter((s) => {
    return s !== sign;
  });
  // Add the clicked sign to the front (most recent first)
  const newHistory = [sign, ...filteredHistory];
  localstorage?.set(CLICK_HISTORY_STORAGE_KEY, newHistory);
};

/**
 * Sort signs based on click history
 * Signs that were clicked appear first (in order of clicks, most recent first)
 * Signs that were never clicked appear after, in their default order
 * @param signs - Array of signs to sort
 * @returns Sorted array with clicked signs first, then unclicked signs
 */
export const sortSignsByClickHistory = (signs: HoroscopeIconType[]): HoroscopeIconType[] => {
  const clickHistory = getClickHistory();
  const clickedSigns = clickHistory.filter((clickedSign) => {
    return signs.includes(clickedSign);
  });
  const unclickedSigns = signs.filter((sign) => {
    return !clickHistory.includes(sign);
  });
  // Return clicked signs first (in order of clicks), then unclicked signs
  return [...clickedSigns, ...unclickedSigns];
};

/**
 * Cached horoscope data is the array [generalData, loveData, moneyData, healthData].
 * Each category response has .items[].horoscope.end (ISO date when content period ends).
 * Returns true only if cache exists and current time is before the content period end.
 * If end is not available, returns false so the app will fetch fresh data.
 */
export const isHoroscopeCacheValid = (cached: unknown): boolean => {
  if (!cached || !Array.isArray(cached) || cached.length === 0) return false;
  const firstCategory = cached[0];
  const firstItem = firstCategory?.items?.[0];
  const end = firstItem?.horoscope?.end;
  if (!end || typeof end !== 'string') return false;
  const endDate = new Date(end);
  if (Number.isNaN(endDate.getTime())) return false;
  return new Date() < endDate;
};

